import * as THREE from '@phdsoft/three';

/**
 * Build the four side planes of the selection pyramid whose apex is the camera
 * and whose cross-section is the drawn rectangle.
 *
 * Every plane is oriented so the rectangle centre — and therefore everything
 * inside the pyramid in front of the camera — lies on its positive side. The
 * mirrored pyramid behind the camera ends up on the negative side of all four
 * planes, so the caller must only accept points with all distances >= 0.
 *
 * @param cameraPosition   world-space camera position (the pyramid apex)
 * @param rectCornersWorld the 4 rectangle corners in world space, in drawing order
 *                         (top-left then anticlockwise, but any consistent order works)
 * @returns the 4 planes, or null for a degenerate (zero-width or zero-height) rectangle, which selects nothing.
 */
export function buildRectangleFrustumPlanes(cameraPosition: THREE.Vector3, rectCornersWorld: THREE.Vector3[]): THREE.Plane[] | null {
  const centre = new THREE.Vector3();
  for (const corner of rectCornersWorld) centre.add(corner);
  centre.multiplyScalar(1 / rectCornersWorld.length);

  // A collapsed edge (zero-height or zero-width drag) gives a zero normal: the
  // plane would accept everything and its orientation would be arbitrary.
  const DEGENERATE_NORMAL_LENGTH_SQ = 1e-12;
  const toA = new THREE.Vector3();
  const toB = new THREE.Vector3();
  const planes: THREE.Plane[] = [];
  for (const [a, b] of [[1, 0], [2, 1], [3, 2], [0, 3]]) { // left, bottom, right, top
    toA.subVectors(rectCornersWorld[a], cameraPosition);
    toB.subVectors(rectCornersWorld[b], cameraPosition);
    const normal = new THREE.Vector3().crossVectors(toB, toA);
    if (normal.lengthSq() < DEGENERATE_NORMAL_LENGTH_SQ) return null;
    planes.push(new THREE.Plane().setFromNormalAndCoplanarPoint(normal.normalize(), cameraPosition));
  }

  for (const p of planes) {
    if (p.distanceToPoint(centre) < 0) p.negate();
  }
  return planes;
}

/** True when the point is inside the selection pyramid in front of the camera; always false for null (degenerate) planes. */
export function isInsideRectangleFrustum(point: THREE.Vector3, planes: THREE.Plane[] | null): boolean {
  if (!planes) return false;
  return planes.every(p => p.distanceToPoint(point) >= 0);
}
